// src/components/CourseEdit.js
import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase-config";
import ScorecardInputTable from "./ScorecardInputTable";

const CourseEdit = () => {
  // State for course search
  const [searchTerm, setSearchTerm] = useState("");
  const [courses, setCourses] = useState([]);
  // When a course is selected for editing
  const [selectedCourse, setSelectedCourse] = useState(null);
  // Editable state for the selected course.
  const [editCourseData, setEditCourseData] = useState(null);
  // Snackbar state for notifications.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch courses from "courses" collection based on search term.
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const coursesRef = collection(db, "courses");
        let q;
        if (searchTerm.trim() !== "") {
          q = query(
            coursesRef,
            where("course_name", ">=", searchTerm),
            where("course_name", "<=", searchTerm + "\uf8ff")
          );
        } else {
          q = coursesRef;
        }
        const querySnapshot = await getDocs(q);
        const coursesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCourses(coursesList);
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };

    fetchCourses();
  }, [searchTerm]);

  // When the admin clicks a course, load it for editing.
  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    setEditCourseData(course);
  };

  // Helper for handling top-level field changes.
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    // Handle nested fields using dot notation.
    if (name.includes(".")) {
      const [parentKey, childKey] = name.split(".");
      setEditCourseData((prev) => ({
        ...prev,
        [parentKey]: { ...prev[parentKey], [childKey]: value },
      }));
    } else {
      setEditCourseData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Helper to update a tee’s hole information.
  // teeType: "male" or "female", teeIndex: index within that array,
  // holeIndex: index of the hole inside the tee's holes, field: "yardage"/"par"/"handicap"
  const handleTeeHoleChange = (teeType, teeIndex, holeIndex, field, value) => {
    setEditCourseData((prev) => {
      const newData = { ...prev };
      if (!newData.tees) newData.tees = {};
      if (!newData.tees[teeType]) newData.tees[teeType] = [];
      const currentTee = { ...(newData.tees[teeType][teeIndex] || {}) };
      const totalHoles = parseInt(newData.number_of_holes, 10) || 18;
      if (!currentTee.holes || currentTee.holes.length !== totalHoles) {
        currentTee.holes = Array.from({ length: totalHoles }, () => ({
          yardage: "",
          par: "",
          handicap: "",
        }));
      }
      currentTee.holes[holeIndex] = {
        ...currentTee.holes[holeIndex],
        [field]: value,
      };
      newData.tees[teeType][teeIndex] = currentTee;
      return newData;
    });
  };

  // Handler to update the course in Firestore.
  const handleUpdateCourse = async () => {
    if (!selectedCourse) return;
    try {
      const courseDocRef = doc(db, "courses", selectedCourse.id);
      await updateDoc(courseDocRef, editCourseData);
      setSnackbar({
        open: true,
        message: "Course updated successfully!",
        severity: "success",
      });
      // Optionally reset state.
      setSelectedCourse(null);
      setEditCourseData(null);
    } catch (error) {
      console.error("Error updating course:", error);
      setSnackbar({
        open: true,
        message: "Failed to update course.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Render search view if no course is selected.
  if (!selectedCourse) {
    return (
      <Paper sx={{ padding: 2, margin: 2 }}>
        <Typography variant="h5" gutterBottom>
          Edit Course
        </Typography>
        <TextField
          label="Search Courses by Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
        />
        {courses.length > 0 ? (
          <Box>
            {courses.map((course) => (
              <ListItem
                key={course.id}
                button
                onClick={() => handleSelectCourse(course)}
              >
                <ListItemText
                  primary={course.course_name}
                  secondary={course.club_name}
                />
              </ListItem>
            ))}
          </Box>
        ) : (
          <Typography variant="body2">No courses found.</Typography>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    );
  }

  // Render edit form when a course is selected.
  return (
    <Paper sx={{ padding: 2, margin: 2 }}>
      <Typography variant="h5" gutterBottom>
        Editing: {editCourseData.course_name}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Top-level course fields */}
        <TextField
          label="Club Name"
          name="club_name"
          value={editCourseData.club_name || ""}
          onChange={handleEditInputChange}
          fullWidth
        />
        <TextField
          label="Course Name"
          name="course_name"
          value={editCourseData.course_name || ""}
          onChange={handleEditInputChange}
          fullWidth
        />
        <TextField
          label="Address"
          name="location.address"
          value={editCourseData?.location?.address || ""}
          onChange={handleEditInputChange}
          fullWidth
        />
        <TextField
          label="City"
          name="location.city"
          value={editCourseData?.location?.city || ""}
          onChange={handleEditInputChange}
          fullWidth
        />
        {/* Additional location fields as needed */}
        <FormControl fullWidth>
          <InputLabel id="hole-count-label">Number of Holes</InputLabel>
          <Select
            labelId="hole-count-label"
            label="Number of Holes"
            name="number_of_holes"
            value={editCourseData.number_of_holes || ""}
            onChange={handleEditInputChange}
          >
            <MenuItem value={9}>9 Holes</MenuItem>
            <MenuItem value={18}>18 Holes</MenuItem>
          </Select>
        </FormControl>

        {/* Editing forms for Male Tees */}
        {editCourseData.tees?.male &&
          editCourseData.tees.male.map((tee, teeIndex) => (
            <Box
              key={`male-${teeIndex}`}
              sx={{
                border: "1px solid #ddd",
                padding: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1">
                Male Tee #{teeIndex + 1}
              </Typography>
              <TextField
                label="Tee Name"
                value={tee.tee_name || ""}
                onChange={(e) =>
                  setEditCourseData((prev) => {
                    const newData = { ...prev };
                    newData.tees.male[teeIndex].tee_name = e.target.value;
                    return newData;
                  })
                }
                fullWidth
              />
              <TextField
                label="Course Rating"
                value={tee.course_rating || ""}
                onChange={(e) =>
                  setEditCourseData((prev) => {
                    const newData = { ...prev };
                    newData.tees.male[teeIndex].course_rating = e.target.value;
                    return newData;
                  })
                }
                fullWidth
              />
              <TextField
                label="Slope Rating"
                value={tee.slope_rating || ""}
                onChange={(e) =>
                  setEditCourseData((prev) => {
                    const newData = { ...prev };
                    newData.tees.male[teeIndex].slope_rating = e.target.value;
                    return newData;
                  })
                }
                fullWidth
              />
              {/* Scorecard Table for this male tee */}
              {editCourseData.number_of_holes && (
                <ScorecardInputTable
                  holes={
                    tee.holes ||
                    Array.from(
                      { length: parseInt(editCourseData.number_of_holes, 10) },
                      () => ({ yardage: "", par: "", handicap: "" })
                    )
                  }
                  handleHoleChange={(holeIndex, field, value) =>
                    handleTeeHoleChange("male", teeIndex, holeIndex, field, value)
                  }
                  holeCount={parseInt(editCourseData.number_of_holes, 10)}
                  holeOffset={0}
                />
              )}
            </Box>
          ))}

        {/* Editing forms for Female Tees */}
        {editCourseData.tees?.female &&
          editCourseData.tees.female.map((tee, teeIndex) => (
            <Box
              key={`female-${teeIndex}`}
              sx={{
                border: "1px solid #ddd",
                padding: 2,
                marginBottom: 2,
              }}
            >
              <Typography variant="subtitle1">
                Female Tee #{teeIndex + 1}
              </Typography>
              <TextField
                label="Tee Name"
                value={tee.tee_name || ""}
                onChange={(e) =>
                  setEditCourseData((prev) => {
                    const newData = { ...prev };
                    newData.tees.female[teeIndex].tee_name = e.target.value;
                    return newData;
                  })
                }
                fullWidth
              />
              <TextField
                label="Course Rating"
                value={tee.course_rating || ""}
                onChange={(e) =>
                  setEditCourseData((prev) => {
                    const newData = { ...prev };
                    newData.tees.female[teeIndex].course_rating = e.target.value;
                    return newData;
                  })
                }
                fullWidth
              />
              <TextField
                label="Slope Rating"
                value={tee.slope_rating || ""}
                onChange={(e) =>
                  setEditCourseData((prev) => {
                    const newData = { ...prev };
                    newData.tees.female[teeIndex].slope_rating = e.target.value;
                    return newData;
                  })
                }
                fullWidth
              />
              {/* Scorecard Table for this female tee */}
              {editCourseData.number_of_holes && (
                <ScorecardInputTable
                  holes={
                    tee.holes ||
                    Array.from(
                      { length: parseInt(editCourseData.number_of_holes, 10) },
                      () => ({ yardage: "", par: "", handicap: "" })
                    )
                  }
                  handleHoleChange={(holeIndex, field, value) =>
                    handleTeeHoleChange("female", teeIndex, holeIndex, field, value)
                  }
                  holeCount={parseInt(editCourseData.number_of_holes, 10)}
                  holeOffset={0}
                />
              )}
            </Box>
          ))}

        <Box sx={{ marginTop: 2 }}>
          <Button variant="contained" onClick={handleUpdateCourse} sx={{ marginRight: 2 }}>
            Update Course
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedCourse(null);
              setEditCourseData(null);
            }}
          >
            Back to Search
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CourseEdit;